import { useState, useCallback } from 'react';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { apiService } from '../../../shared/services/api';
import { useErrors } from '../../../shared/contexts/ErrorContext';

interface UseLoginResult {
  login: (username: string, password: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

export function useLogin(): UseLoginResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { login: setAuth } = useAuth();
  const { addError } = useErrors();

  const login = useCallback(
    async (username: string, password: string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await apiService.login({ username, password });

        if (response.success && response.data) {
          const { accessToken, refreshToken, user } = response.data;
          setAuth(accessToken, refreshToken, {
            id: user.id,
            username: user.username,
            role: user.accessRights,
            cid: user.cid,
          });
          return true;
        } else {
          const errorMsg = 'Login failed';
          setError(errorMsg);
          addError(errorMsg);
          return false;
        }
      } catch (err: unknown) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
              'Login failed. Please check your credentials.';
        setError(errorMessage);
        addError(errorMessage);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [setAuth, addError]
  );

  return { login, isLoading, error };
}
